import { AngleUnits } from 'convert-units/definitions/angle';

import {
    ExtendedPressureUnits,
    ExtendedSpeedUnits,
    ExtendedTemperatureUnits,
    SalinityUnits,
    Units,
} from '@/common/convert';
import { APIResponse, LocationData } from '@/common/types';
import { QuantityType } from '@/components/data/Datum';
import { DateRange } from '@/store';
import { BreachedSensorAlert } from '@/utils';

export type ProcessingSource = 'embedded' | 'hdr';

export type HistoricalWaveDataItem = {
    significantWaveHeight: number;
    peakPeriod: number;
    meanPeriod: number;
    peakDirection: number;
    peakDirectionalSpread: number;
    meanDirection: number;
    meanDirectionalSpread: number;
    latitude: number;
    longitude: number;
    timestamp: string;
    processing_source: ProcessingSource;
};

export type WaveDataItemFormattedWithUnits = HistoricalWaveDataItem & {
    significantWaveHeightUnits: string;
    significantWaveHeightUnitType: 'length';
};

export type HistoricalWindDataItem = {
    speed: number;
    direction: number;
    seasurfaceId: number;
    location: LocationData;
    processing_source: ProcessingSource;
};
/**
 * A wind sample with the timestamp both date bounds filter on lifted out of `location`, which is where
 * the endpoint puts it. The windows a range is split into are cut on that timestamp.
 */
export type WindHistoryItem = Omit<HistoricalWindDataItem, 'location'> & LocationData;

export type WindDataItemFormattedWithUnits = Omit<HistoricalWindDataItem, 'location'> & {
    windSpeedUnits: string;
    windSpeedUnitType: 'speed';
    timestamp: string;
};

export type PostHistoricalWindDataResponse = APIResponse<{ windHistory: HistoricalWindDataItem[] }>;
export type PostHistoricalWindDataPayload = {
    spotterId: string;
    startDate: string;
    endDate: string;
    processingSources: ProcessingSource | 'all';
};

export type HistoricalSurfaceTemperatureDataItem = {
    degrees: number;
    location: LocationData;
    processing_source: ProcessingSource;
};

/**
 * A sea surface temperature sample with the timestamp both date bounds filter on lifted out of
 * `location`, which is where the endpoint puts it. The windows a range is split into are cut on that
 * timestamp.
 */
export type SurfaceTemperatureHistoryItem = Omit<HistoricalSurfaceTemperatureDataItem, 'location'> & LocationData;

export type SeaSurfaceTemperatureItemFormattedWithUnits = {
    degrees: number;
    timestamp: string;
    processing_source: ProcessingSource;
    units: string;
    unitType: 'temperature';
};

export type PostHistoricalSurfaceTemperatureDataResponse = APIResponse<{
    sstHistory: HistoricalSurfaceTemperatureDataItem[];
}>;
export type PostHistoricalSurfaceTemperatureDataPayload = {
    spotterId: string;
    startDate: string;
    endDate: string;
    processingSources: ProcessingSource | 'all';
};

export type HistoricalBarometricPressureItem = {
    data_type_name: string;
    latitude: number;
    longitude: number;
    processing_source: ProcessingSource;
    timestamp: string;
    unit_type: string;
    units: string;
    value: number;
};

export type BaroPressureItemFormattedWithUnits = HistoricalBarometricPressureItem & {
    unitType: 'airPressure';
};

export type HistoricalSensorDataItem<Value = number> = {
    latitude: number;
    longitude: number;
    timestamp: string;
    sensorPosition: number;
    units: string;
    value: Value;
    unit_type: string;
    data_type_name: string;
};

export type RawSensorData<V = number> = {
    bristlemouth_node_id?: string;
    data_type_name: string;
    configuration_checksum: string;
    latitude: number;
    longitude: number;
    sensorPosition: number;
    timestamp: string;
    unit_type: QuantityType;
    units: Units;
    value: V;
};

/**
 * The `data_type_name` a per-second sound-pressure row carries.
 *
 * One-per-second band rows are the bulk of a hydrophone window: each carries an array of 28 band SPLs, so a
 * consumer reading only `SPL_BROADBAND_READING_KEYS` can have them dropped before they are cached. The
 * `bm_borealis_spl_band_stats*` sample rows carry their own bands and are a separate vocabulary.
 */
export const SPL_BAND_SPL_READING_KEYS: readonly string[] = [
    'bm_borealis_band_spls_reading',
    'bm_borealis2_band_spls_reading',
];
export const SPL_BROADBAND_READING_KEYS: readonly string[] = [
    'bm_borealis_spl_broadband_reading',
    'bm_borealis2_spl_broadband_reading',
];
export const SPL_READING_KEYS: readonly string[] = [...SPL_BAND_SPL_READING_KEYS, ...SPL_BROADBAND_READING_KEYS];

export type SensorDatumValue<V = number> = { value: V; units: Units; unitType: QuantityType };

export type ConsolidatedSensorDatum = {
    latitude: number;
    longitude: number;
    sensorPosition: number;
    sensorConfigChecksum: string;
    bristlemouthNodeId?: string;
    timestamp: string;
    data: { [dataKey: string]: SensorDatumValue };
};

// keys should be in priority order!
export const getDatumByAnyKey = (
    data: ConsolidatedSensorDatum['data'],
    keys: readonly string[]
): SensorDatumValue | undefined => {
    const presentKey = keys.find((key) => key in data);

    if (!presentKey) {
        return undefined;
    }

    return data[presentKey];
};

export type HistoricalSensorData = { [sensorPosition: string]: ConsolidatedSensorDatum[] };

export type PartitionDataItem = {
    startFrequency: number;
    endFrequency: number;
    significantWaveHeight: number;
    meanPeriod: number;
    meanDirection: number;
    meanDirectionalSpread: number;
};
export type PartitionHistoryDataItem = {
    partitions: [PartitionDataItem, PartitionDataItem];
    location: LocationData;
    processing_source: ProcessingSource;
};

export type PartitionWaveDataItemFormattedWithUnits = {
    significantWaveHeightSwell: number;
    meanPeriodSwell: number;
    meanDirectionSwell: number;
    meanDirectionalSpreadSwell: number;
    significantWaveHeightSea: number;
    meanPeriodSea: number;
    meanDirectionSea: number;
    meanDirectionalSpreadSea: number;
    timestamp: string;
    processing_source: ProcessingSource;
    partitionSignificantWaveHeightUnits: string;
    partitionSignificantWaveHeightUnitType: 'length';
};

export type ConsolidatedWaveDataItem = PartitionWaveDataItemFormattedWithUnits & WaveDataItemFormattedWithUnits;

export type GetPartitionHistoryDataResponse = APIResponse<{ partitionHistory: PartitionHistoryDataItem[] }>;
export type GetPartitionHistoryDataParams = {
    spotterId: string;
    startDate: string;
    endDate: string;
    processingSources: ProcessingSource | 'all';
};

/**
 * A partition sample with the timestamp both date bounds filter on lifted out of `location`, which is
 * where the endpoint puts it. The windows a range is split into are cut on that timestamp.
 */
export type PartitionHistoryItem = Omit<PartitionHistoryDataItem, 'location'> & LocationData;

/*
    Hydrophone Data
*/
export type GetHistoricalHydrophoneDataParams = {
    spotterId: string;
    dateRange: DateRange | null;
};

export type GetHydrophoneDataResponse = APIResponse<SpectrogramDatum[]>;

export type SpectrogramDatum = {
    timestamp: string;
    frequency: number;
    median: number;
};

/*
    Sound Pressure Data
*/
export type SoundPressureBandStatsSample = {
    dataQuality: 'present';
    processing_source: ProcessingSource;
    timestamp: string;
    sensorPosition: string;
    sampleIntervalMinutes?: number;
    bandStats: SoundPressureSampleStatsDatum[];
    broadbandSpl: number;
};

export type SoundPressureMissingSample = {
    dataQuality: 'absent';
    timestamp: string;
    sensorPosition: string;
    sampleIntervalMinutes?: number;
};

export type SoundPressureQuietSample = {
    dataQuality: 'tooQuiet';
    processing_source: ProcessingSource;
    timestamp: string;
    sensorPosition: string;
    sampleIntervalMinutes?: number;
    broadbandSpl: number;
};

export type SoundPressureSample = SoundPressureBandStatsSample | SoundPressureMissingSample | SoundPressureQuietSample;

export type SoundPressureSampleStatsDatum = {
    min?: number; // bottom whisker
    lower: number; // bottom of box
    mean: number; // line
    upper: number; // top of box
    max?: number; // top whisker
    median: number; // box dash
    frequency: number; // frequency in Hz
    bandNumber: number; // band number
};

export type SoundPressureReading = {
    timestamp: string;
    sensorPosition: string;
    bristlemouthNodeId?: string;
    broadbandSpl: number;
    bands: {
        spl: number; // SPL in dB re µPa²
        frequency: number; // frequency in Hz
        bandNumber: number; // band number
    }[];
};

export type SPLRawBandStats = {
    '1st_percentile'?: number[];
    '25th_percentile': number[];
    '50th_percentile': number[];
    '75th_percentile': number[];
    '99th_percentile'?: number[];
    band_number: number[];
    frequency: number[];
    mean: number[];
};

export type PerSensor<T> = { [sensorPosition: string]: T };

export type CurrentsDatum = {
    timestamp: string;
    speed: number;
    speedUnits: ExtendedSpeedUnits;
    speedUnitType: 'currentSpeed';
    speedStdDev: number;
    direction: number;
    directionUnits: AngleUnits;
    directionUnitType: 'angle';
    sensorTilt: number;
    sensorTiltUnits: AngleUnits;
    sensorTiltUnitType: 'angle';
};

export type DissolvedOxygenDatum = {
    timestamp: string;
    concentration: number;
    concentrationUnits: string;
    concentrationUnitType: string;
    saturation: number;
    saturationUnits: string;
    saturationUnitType: string;
    quality: number;
    qualityUnits: string;
    qualityUnitType: string;
};

export type TemperatureDatum = {
    timestamp: string;
    temperature: number;
    units: ExtendedTemperatureUnits;
    unitType: 'temperature';
};

export type PressureDatum = {
    timestamp: string;
    pressure: number;
    units: ExtendedPressureUnits;
    unitType: 'pressure';
};

export type TurbidityDatum = {
    timestamp: string;
    turbidity: number;
    units: string;
    unitType: 'turbidity';
};

export type SalinityDatum = {
    timestamp: string;
    salinity: number;
    salinityStdDev: number;
    units: SalinityUnits;
    unitType: 'salinity';
};

/*
    Estimated Wave Spectrum Data
*/
export type WaveSpectrumDatum = { freq: number; dir: number; energy: number };

export type WaveFrequencyRawDatum = {
    frequency: number[];
    df: number[];
    a1: number[];
    b1: number[];
    a2: number[];
    b2: number[];
    varianceDensity: number[];
    direction: number[];
    directionalSpread: number[];
    processing_source: string;
} & LocationData;

export type WaveFrequency = {
    frequency: number[];
    df: number[];
    a1: number[];
    b1: number[];
    a2: number[];
    b2: number[];
    varianceDensity: number[];
    direction: number[];
};

export type WaveSpectrumDataByTimestamp = Record<string, WaveFrequency>;
type WaveSpectrumData = {
    spotterId: string;
    limit: number;
    frequencyData: WaveFrequencyRawDatum[];
};

export type GetWaveSpectrumDataRawResponse = APIResponse<WaveSpectrumData>;

/*
    Wave Energy Spectrum Data
*/

/**
 * Represents the response for wave energy spectrum data.
 *
 * @property directions - Array of 180 possible directions in degrees. Degree resolution is set in lambda function.
 * @property frequencies - Array of frequencies in Hz.
 * @property energies - 2D array where `energies[f][d]` gives the energy at frequency `f` and direction `d`.
 */
export type WaveEnergySpectrumResponse = {
    directions: number[];
    energies: number[][];
    frequencies: number[];
};

export type FakeDetectionClassification = 'splBreach'; // not real detections - these are computed client side
export type DetectionClassification = 'vessels' | 'dolphins' | 'boats' | FakeDetectionClassification;
export type DetectionTimeResolution = 'low' | 'high';

export type Detection = {
    classification: DetectionClassification;
    time_resolution: DetectionTimeResolution;
    start_time: string; // ISO timestamp
    end_time: string; // ISO timestamp
    latitude: number;
    longitude: number;
    density: number;
    density_threshold: number;
    is_detection: boolean;
    bristlemouth_node_id: string;
    sensor_position: number;
    breachedSensorAlert?: BreachedSensorAlert; // only present on dashboard-side derived alerts
};
